"use client";

import React, { Suspense } from "react";
import { getComponent } from "./ComponentsMapper";
import { LayoutType, LayoutSlot, LayoutItem, isApiComponent, LayoutRowConfig } from "./LayoutTokens";
import { spacingMap, backgroundMap, widthMap } from "./layoutStyles";
import { ErrorBoundary } from "react-error-boundary";

export const LayoutRow = ({
  layoutType = "full",
  spacing = "none",
  background = "none",
  width = "full",
  className = "",
  sectionClassName = "",
  innerClassName = "",
  colsClassName = "",
  slots,
}: LayoutRowConfig) => {
  return (
    <div className={`${backgroundMap[background]} ${spacingMap[spacing]} ${className}`}>
      <section className={`${widthMap[width]} ${sectionClassName}`}>
        {renderLayout(layoutType, slots, innerClassName, colsClassName)}
      </section>
    </div>
  );
};

const renderItem = (item: LayoutItem, key: number) => {
  if (!item) return null;

  if (isApiComponent(item)) {
    return (
      <ErrorBoundary key={item.id} fallback={<div>An unexpected error happened trying to render this component</div>}>
        <Suspense>{getComponent(item)}</Suspense>
      </ErrorBoundary>
    );
  }

  // JSX element / ReactNode
  return (
    <ErrorBoundary key={key} fallback={<div>An unexpected error happened</div>}>
      {item}
    </ErrorBoundary>
  );
};

const renderLayout = (
  layoutType: LayoutType,
  slots: Partial<Record<LayoutSlot, LayoutItem[]>>,
  innerClassName: string,
  colsClassName: string
) => {
  switch (layoutType) {
    case "66/33":
      return (
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 ${innerClassName}`}>
          <div className={`lg:col-span-2 ${colsClassName}`}>{slots.main?.map(renderItem)}</div>

          <div className={`lg:col-span-1 ${colsClassName}`}>{slots.side?.map(renderItem)}</div>
        </div>
      );

    case "50/50":
      return (
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-6 ${innerClassName}`}>
          {(["col1", "col2"] as LayoutSlot[]).map(slot => (
            <div key={slot} className={`${colsClassName}`}>
              {slots[slot]?.map(renderItem)}
            </div>
          ))}
        </div>
      );

    case "33/66":
      return (
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 ${innerClassName}`}>
          <div className={`lg:col-span-1 ${colsClassName}`}>{slots.side?.map(renderItem)}</div>
          <div className={`lg:col-span-2 ${colsClassName}`}>{slots.main?.map(renderItem)}</div>
        </div>
      );

    case "33/33/33":
      return (
        <div className={`grid grid-cols-1 lg:grid-cols-3 gap-4 lg:gap-6 ${innerClassName}`}>
          {(["col1", "col2", "col3"] as LayoutSlot[]).map(slot => (
            <div key={slot} className={`${colsClassName}`}>
              {slots[slot]?.map(renderItem)}
            </div>
          ))}
        </div>
      );

    case "25/25/25/25":
      return (
        <div className={`grid grid-cols-1 lg:grid-cols-4 gap-6 ${innerClassName}`}>
          {(["col1", "col2", "col3", "col4"] as LayoutSlot[]).map(slot => (
            <div key={slot} className={`${colsClassName}`}>
              {slots[slot]?.map(renderItem)}
            </div>
          ))}
        </div>
      );

    case "empty":
      return <>{slots.main?.map(renderItem)}</>;

    case "full":
    default:
      return (
        <div className={`${innerClassName}`}>
          <div className={`${colsClassName}`}>{slots.main?.map(renderItem)}</div>
        </div>
      );
  }
};
